/**
 * siteAnalytics.ts — client-side visitor + time-on-page telemetry.
 *
 * Emits anonymous page-view, time-on-page, and session events to
 * /api/ops/client-event. Event shapes must match the server contract exactly
 * (see functions/api/ops/client-event.js). visitorId/sessionId are random
 * local UUIDs; nothing derived from identity. Owns the shared event queue;
 * errorReporter.ts enqueues client.error events through this module.
 */

const CLIENT_EVENT_URL = '/api/ops/client-event';
const FLUSH_INTERVAL_MS = 30_000;
const MAX_BATCH = 50;
const VISITOR_KEY = 'vip_visitorId';
const SESSION_KEY = 'vip_sessionId';

export interface SitePageTimeEvent {
  event: 'site.page.time';
  route: string;
  durationMs: number;
  visitorId: string;
  sessionId: string;
  ts: number;
}
export interface SiteSessionEvent {
  event: 'site.session';
  route: string;
  durationMs: number;
  visitorId: string;
  sessionId: string;
  ts: number;
}
export interface ClientErrorEvent {
  event: 'client.error';
  route: string;
  message: string;
  frame: string;
  kind: string;
  visitorId: string;
  ts: number;
}
export interface ComputeMetricsFallbackEvent {
  event: 'compute.metrics.fallback';
  route: string;
  endpoint: string;
  reason: string;
  status?: number;
  visitorId: string;
  ts: number;
}
export type ClientEvent =
  | SitePageTimeEvent
  | SiteSessionEvent
  | ClientErrorEvent
  | ComputeMetricsFallbackEvent;

let visitorId = '';
let sessionId = '';
let queue: ClientEvent[] = [];
let currentRoute = '';
let routeEnteredAt = 0;
let sessionStartedAt = 0;
let sessionEnded = false;
let started = false;

function uuid(): string {
  const c = (typeof globalThis !== 'undefined' ? globalThis : window).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  if (c && typeof c.getRandomValues === 'function') {
    const b = new Uint8Array(16);
    c.getRandomValues(b);
    return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function readOrCreate(store: Storage, key: string): string {
  try {
    const existing = store.getItem(key);
    if (existing) return existing;
    const id = uuid();
    store.setItem(key, id);
    return id;
  } catch {
    // Storage blocked (private mode / disabled) — in-memory id for this load.
    return uuid();
  }
}

export function getVisitorId(): string {
  return visitorId;
}

export function enqueueClientEvent(ev: ClientEvent): void {
  if (!started) return;
  queue.push(ev);
  if (queue.length >= MAX_BATCH) flush(false);
}

/**
 * Record that an AWS Lambda compute call degraded to the in-browser TS
 * fallback. The ONLY client-side signal for when the Lambda returned 200 but
 * the body failed Zod validation (server-side telemetry records that as 'ok').
 */
export function recordComputeFallback(endpoint: string, reason: string, status?: number): void {
  enqueueClientEvent({
    event: 'compute.metrics.fallback',
    route: typeof location !== 'undefined' ? location.pathname : '-',
    endpoint,
    reason: String(reason ?? '').slice(0, 200),
    status,
    visitorId,
    ts: Date.now(),
  });
}

function flush(useBeacon: boolean): void {
  if (!queue.length) return;
  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(batch.length);
  const body = JSON.stringify({ events: batch });
  try {
    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(CLIENT_EVENT_URL, new Blob([body], { type: 'application/json' }));
    } else {
      void fetch(CLIENT_EVENT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => { /* best-effort telemetry */ });
    }
  } catch {
    // Re-queue the batch so the next flush retries it.
    queue = batch.concat(queue);
  }
}

/**
 * Record a navigation. Closes the previous route with a `site.page.time`
 * event and starts timing the new route.
 */
export function recordPageView(route: string): void {
  if (!started) return;
  const now = Date.now();
  if (currentRoute && routeEnteredAt) {
    enqueueClientEvent({
      event: 'site.page.time',
      route: currentRoute,
      durationMs: now - routeEnteredAt,
      visitorId,
      sessionId,
      ts: now,
    });
  }
  currentRoute = route;
  routeEnteredAt = now;
}

function endSession(): void {
  if (sessionEnded) {
    flush(true);
    return;
  }
  sessionEnded = true;
  const now = Date.now();
  if (currentRoute && routeEnteredAt) {
    enqueueClientEvent({
      event: 'site.page.time',
      route: currentRoute,
      durationMs: now - routeEnteredAt,
      visitorId,
      sessionId,
      ts: now,
    });
  }
  if (sessionStartedAt) {
    enqueueClientEvent({
      event: 'site.session',
      route: currentRoute || '/',
      durationMs: now - sessionStartedAt,
      visitorId,
      sessionId,
      ts: now,
    });
  }
  flush(true);
}

/** One-time setup: resolve ids, start the session timer, install handlers. Idempotent. */
export function initSiteAnalytics(): void {
  if (started || typeof window === 'undefined') return;
  visitorId = readOrCreate(window.localStorage, VISITOR_KEY);
  sessionId = readOrCreate(window.sessionStorage, SESSION_KEY);
  started = true;
  sessionStartedAt = Date.now();

  window.setInterval(() => flush(false), FLUSH_INTERVAL_MS);
  window.addEventListener('pagehide', endSession);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
}
